import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../common/Layout";
import LoadingSpinner from "../common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import analyticsService from "../../services/analyticsService";
import RepLeaderboardFull from "./RepLeaderboardFull";
import SourcePerformanceDetail from "./SourcePerformanceDetail";
import ReminderResponsiveness from "./ReminderResponsiveness";
import StaleLeadsHeatmap from "./StaleLeadsHeatmap";
import OverdueHeatmap from "./OverdueHeatmap";

const MANAGER_ROLES = ["Admin", "SM", "Sales Owner", "Sales Manager"];

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}
function isoToday() { return new Date().toISOString().slice(0, 10); }

export default function ManagerAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const role = String(user?.role || "").trim();
  const allowed = MANAGER_ROLES.includes(role);

  const [from, setFrom] = useState(params.get("from") || isoDaysAgo(30));
  const [to, setTo] = useState(params.get("to") || isoToday());
  const [source, setSource] = useState(params.get("source") || "all");
  const [salesRep, setSalesRep] = useState(params.get("salesRep") || "all");
  const [filterData, setFilterData] = useState({ sources: [], reps: [] });

  const [leaderboard, setLeaderboard] = useState([]);
  const [sourcePerf, setSourcePerf] = useState([]);
  const [responsiveness, setResponsiveness] = useState({ weekly: [], per_rep: [] });
  const [stale, setStale] = useState({ buckets: [], data: [] });
  const [overdue, setOverdue] = useState({ days: [], data: [] });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError("");
    const common = { from, to, source, salesRep };
    try {
      const [lb, sp, rr, sh, oh, fd] = await Promise.all([
        analyticsService.getRepLeaderboard(common),
        analyticsService.getSourcePerformance(common),
        analyticsService.getReminderResponsiveness(common),
        analyticsService.getStaleLeadsHeatmap(common),
        analyticsService.getOverdueHeatmap(common),
        analyticsService.getFilters(),
      ]);
      setLeaderboard(lb?.data || []);
      setSourcePerf(sp?.data || []);
      setResponsiveness({ weekly: rr?.weekly || [], per_rep: rr?.per_rep || [] });
      setStale({ buckets: sh?.buckets || [], data: sh?.data || [] });
      setOverdue({ days: oh?.days || [], data: oh?.data || [] });
      setFilterData(fd || { sources: [], reps: [] });
    } catch {
      setError("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [allowed, from, to, source, salesRep]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (from) next.set("from", from);
    if (to) next.set("to", to);
    if (source && source !== "all") next.set("source", source);
    if (salesRep && salesRep !== "all") next.set("salesRep", salesRep);
    setParams(next, { replace: true });
  }, [from, to, source, salesRep, setParams]);

  const drill = (extra) => {
    const s = { ...extra };
    if (source && source !== "all") s.leadSource = source;
    if (salesRep && salesRep !== "all") s.salesRep = salesRep;
    navigate("/leads", { state: { filters: s } });
  };

  if (!allowed) {
    return (
      <Layout title="Analytics" subtitle="Restricted">
        <div className="empty-state">
          <i className="fas fa-lock" />
          <h3>Not available</h3>
          <p>Analytics is available to managers and admins only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Analytics" subtitle="Rep, source, and reminder performance">
      <div className="r6-analytics-page">
        <div className="r6-analytics-filters">
          <label>
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label>
            Source
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="all">All sources</option>
              {(filterData.sources || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label>
            Rep
            <select value={salesRep} onChange={(e) => setSalesRep(e.target.value)}>
              <option value="all">All reps</option>
              {(filterData.reps || []).map((r) => (
                <option key={r.user_id} value={r.user_id}>{r.full_name}</option>
              ))}
            </select>
          </label>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            <i className="fas fa-sync" /> Refresh
          </button>
        </div>

        {loading ? (
          <LoadingSpinner message="Loading analytics…" />
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <RepLeaderboardFull rows={leaderboard} onDrill={drill} />
            <SourcePerformanceDetail rows={sourcePerf} onDrill={drill} />
            <ReminderResponsiveness
              weekly={responsiveness.weekly}
              perRep={responsiveness.per_rep}
              onDrill={drill}
            />
            <StaleLeadsHeatmap buckets={stale.buckets} rows={stale.data} onDrill={drill} />
            <OverdueHeatmap days={overdue.days} rows={overdue.data} onDrill={drill} />
          </>
        )}
      </div>
    </Layout>
  );
}